import { useEffect, useState } from "react";
import { Plus, Loader2, Download, Pencil, Trash2, FileText } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const PLACEHOLDERS = [
  "seafarer_name", "rank", "vessel_name", "cdc_number", "passport_number", "basic_wages", "contract_duration",
  "joining_port", "joining_date", "owner_name", "seafarer_address", "nok_name", "nok_address", "nok_mobile",
  "port_of_engagement", "home_port_airport",
];

function useOwnerOptions(open, tpl, templates) {
  const [vesselOwners, setVesselOwners] = useState([]);
  useEffect(() => {
    if (open) api.get("/vessels").then(({ data }) => setVesselOwners(data.map((v) => v.owner_name))).catch(() => setVesselOwners([]));
  }, [open]);
  const taken = new Set(templates.filter((t) => t.id !== tpl?.id).map((t) => t.owner_name.trim().toLowerCase()));
  const seen = new Map();
  [...(tpl ? [tpl.owner_name] : []), ...vesselOwners].forEach((o) => {
    const k = (o || "").trim().toLowerCase();
    if (k && !seen.has(k)) seen.set(k, o.trim());
  });
  return [...seen.entries()]
    .map(([k, label]) => ({ value: label, disabled: taken.has(k) }))
    .sort((a, b) => a.disabled - b.disabled || a.value.localeCompare(b.value));
}

function TemplateDialog({ open, onOpenChange, tpl, templates, onSaved }) {
  const [owner, setOwner] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const owners = useOwnerOptions(open, tpl, templates);

  useEffect(() => { if (open) { setOwner(tpl?.owner_name || ""); setFile(null); } }, [open, tpl]);

  const submit = async (e) => {
    e.preventDefault();
    if (!owner) { toast.error("Select an owner"); return; }
    if (!tpl && !file) { toast.error("Choose a .docx file"); return; }
    if (file && !file.name.toLowerCase().endsWith(".docx")) { toast.error("Only Word .docx files are allowed"); return; }
    const fd = new FormData();
    fd.append("owner_name", owner);
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      const cfg = { headers: { "Content-Type": "multipart/form-data" } };
      const { data } = tpl ? await api.put(`/sea-templates/${tpl.id}`, fd, cfg) : await api.post("/sea-templates", fd, cfg);
      toast.success(tpl ? "Template updated" : "Template uploaded");
      if (data.missing_placeholders?.length) toast.warning(`${data.missing_placeholders.length} of 16 placeholders are missing from this template — hover the row's count to see which`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="template-form-dialog">
        <DialogHeader>
          <DialogTitle>{tpl ? "Edit SEA template" : "Upload SEA template"}</DialogTitle>
          <DialogDescription>One template per owner. Matches the vessel's owner name exactly (capitals ignored).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Owner name</Label>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger data-testid="template-owner-select"><SelectValue placeholder={owners.length ? "Select a vessel owner" : "No vessel owners found"} /></SelectTrigger>
              <SelectContent>
                {owners.map((o) => (
                  <SelectItem key={o.value} value={o.value} disabled={o.disabled} data-testid={`template-owner-option-${o.value}`}>
                    {o.value}{o.disabled ? " — template exists" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {owners.length === 0 && <div className="text-xs text-slate-500" data-testid="template-no-owners">Add a vessel with its owner name first.</div>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tpl-file">Word file (.docx){tpl && <span className="text-slate-400 font-normal"> — optional, replaces current file</span>}</Label>
            <Input id="tpl-file" type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="template-file-input" />
          </div>
          <div className="text-xs text-slate-500 leading-relaxed">
            Placeholders: {PLACEHOLDERS.map((p) => <code key={p} className="mr-1.5 text-[11px] bg-slate-100 px-1 rounded">{`{${p}}`}</code>)}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="template-form-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : tpl ? "Save changes" : "Upload template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function downloadTemplate(t) {
  try {
    const { data } = await api.get(`/sea-templates/${t.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = t.file_name || "template.docx";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    toast.error("Could not download the template");
  }
}

function TemplateRow({ t, onEdit, onDelete }) {
  const found = t.placeholders_found.length;
  return (
    <tr className="border-t border-slate-100" data-testid={`template-row-${t.id}`}>
      <td className="px-4 py-3.5 font-semibold text-slate-800" data-testid={`template-owner-${t.id}`}>
        {t.owner_name} {t.is_demo && <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Demo</span>}
      </td>
      <td className="px-4 py-3.5 text-slate-600 font-mono text-xs">{t.file_name}</td>
      <td className="px-4 py-3.5">
        <span className={`text-xs font-medium ${found === 16 ? "text-emerald-700" : "text-amber-700"}`} title={t.missing_placeholders.join(", ")} data-testid={`template-placeholders-${t.id}`}>
          {found}/16 placeholders
        </span>
      </td>
      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}</td>
      <td className="px-4 py-3.5 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={() => downloadTemplate(t)} data-testid={`template-download-btn-${t.id}`}><Download className="w-3.5 h-3.5 mr-1" /> .docx</Button>
        <Button size="icon" variant="ghost" onClick={() => onEdit(t)} data-testid={`template-edit-btn-${t.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => onDelete(t)} data-testid={`template-delete-btn-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
      </td>
    </tr>
  );
}

export default function SeaTemplatesPage() {
  const { user } = useAuth();
  if (!["Super Admin", "Management/HOD"].includes(user?.role?.role_name)) {
    return <div className="py-20 text-center text-slate-500" data-testid="sea-templates-forbidden">Only Super Admin or Management can manage SEA templates.</div>;
  }
  return <SeaTemplatesInner />;
}

function SeaTemplatesInner() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, tpl: null });
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sea-templates"); setItems(data); } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async () => {
    const t = toDelete;
    setToDelete(null);
    try { await api.delete(`/sea-templates/${t.id}`); toast.success("Template deleted"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="sea-templates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Settings</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>SEA Templates</h1>
          <div className="text-sm text-slate-500 mt-1">Word templates per owner, used to auto-generate seafarer contracts.</div>
        </div>
        <Button onClick={() => setDialog({ open: true, tpl: null })} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-1" /> Upload template
        </Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="templates-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Owner", "File", "Placeholders", "Updated", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>{!loading && items.map((t) => <TemplateRow key={t.id} t={t} onEdit={(tpl) => setDialog({ open: true, tpl })} onDelete={setToDelete} />)}</tbody>
          </table>
        </div>
        {loading && <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {!loading && items.length === 0 && (
          <div className="py-14 text-center" data-testid="templates-empty-state">
            <FileText className="w-8 h-8 text-slate-300 mx-auto" />
            <div className="text-sm text-slate-500 mt-2">No templates yet.</div>
          </div>
        )}
      </div>
      <TemplateDialog open={dialog.open} onOpenChange={(o) => setDialog((s) => ({ ...s, open: o }))} tpl={dialog.tpl} templates={items} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="template-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the {toDelete?.owner_name} template?</AlertDialogTitle>
            <AlertDialogDescription>Vessels with this owner won't be able to generate SEAs until a new template is uploaded.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="template-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="template-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
